import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { UserCircleIcon, PlusIcon, KeyIcon, TrashIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserManagement = () => {
  const { token, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  const [createForm, setCreateForm] = useState({
    username: '',
    email: '',
    full_name: '',
    password: '',
    confirmPassword: '',
    role: 'operator'
  });

  const [passwordForm, setPasswordForm] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Errore caricamento utenti:', error);
      toast.error('Errore nel caricamento degli utenti');
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) errors.push('almeno 8 caratteri');
    if (!/[A-Z]/.test(password)) errors.push('una maiuscola');
    if (!/[a-z]/.test(password)) errors.push('una minuscola');
    if (!/[0-9]/.test(password)) errors.push('un numero');
    if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)) errors.push('un carattere speciale');
    return errors;
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();

    // Validazioni
    const passwordErrors = validatePassword(createForm.password);
    if (passwordErrors.length > 0) {
      toast.error(`Password deve contenere: ${passwordErrors.join(', ')}`);
      return;
    }

    if (createForm.password !== createForm.confirmPassword) {
      toast.error('Le password non coincidono');
      return;
    }

    try {
      await axios.post(
        `${API}/users/create`,
        {
          username: createForm.username,
          email: createForm.email,
          full_name: createForm.full_name,
          password: createForm.password,
          role: createForm.role
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Utente creato con successo');
      setIsCreateModalOpen(false);
      setCreateForm({
        username: '',
        email: '',
        full_name: '',
        password: '',
        confirmPassword: '',
        role: 'operator'
      });
      await loadUsers();
    } catch (error) {
      console.error('Errore creazione utente:', error);
      toast.error(error.response?.data?.detail || 'Errore nella creazione dell\'utente');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    // Validazioni
    const passwordErrors = validatePassword(passwordForm.new_password);
    if (passwordErrors.length > 0) {
      toast.error(`Nuova password deve contenere: ${passwordErrors.join(', ')}`);
      return;
    }

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Le password non coincidono');
      return;
    }

    try {
      await axios.put(
        `${API}/users/change-password`,
        {
          old_password: passwordForm.old_password,
          new_password: passwordForm.new_password
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Password cambiata con successo');
      setIsPasswordModalOpen(false);
      setPasswordForm({
        old_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      console.error('Errore cambio password:', error);
      toast.error(error.response?.data?.detail || 'Errore nel cambio password');
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Sei sicuro di voler eliminare l'utente "${username}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Utente eliminato con successo');
      await loadUsers();
    } catch (error) {
      console.error('Errore eliminazione utente:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione dell\'utente');
    }
  };

  const getPasswordStrength = (password) => {
    const errors = validatePassword(password);
    if (errors.length === 0) return { label: 'Forte', color: 'text-green-600', bg: 'bg-green-100' };
    if (errors.length <= 2) return { label: 'Media', color: 'text-yellow-600', bg: 'bg-yellow-100' };
    return { label: 'Debole', color: 'text-red-600', bg: 'bg-red-100' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Caricamento utenti...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestione Utenti</h1>
          <p className="text-gray-600">Amministra utenti e permessi del sistema</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setIsPasswordModalOpen(true)}>
            <KeyIcon className="w-4 h-4 mr-2" />
            Cambia Password
          </Button>
          <Button onClick={() => setIsCreateModalOpen(true)}>
            <PlusIcon className="w-4 h-4 mr-2" />
            Nuovo Utente
          </Button>
        </div>
      </div>

      {/* Users List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {users.map((user) => (
          <Card key={user.id} className={user.id === currentUser?.id ? 'border-blue-500' : ''}>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    {user.role === 'admin' ? (
                      <ShieldCheckIcon className="w-6 h-6 text-blue-600" />
                    ) : (
                      <UserCircleIcon className="w-6 h-6 text-blue-600" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-gray-900">{user.full_name}</h3>
                      {user.id === currentUser?.id && (
                        <Badge variant="outline">Tu</Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">@{user.username}</p>
                    <p className="text-sm text-gray-600">{user.email}</p>
                    <div className="mt-2">
                      <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                        {user.role === 'admin' ? 'Amministratore' : 'Operatore'}
                      </Badge>
                      {user.is_active ? (
                        <Badge variant="outline" className="ml-2">Attivo</Badge>
                      ) : (
                        <Badge variant="destructive" className="ml-2">Disattivato</Badge>
                      )}
                    </div>
                  </div>
                </div>

                {user.id !== currentUser?.id && (
                  <button
                    onClick={() => handleDeleteUser(user.id, user.username)}
                    className="p-2 hover:bg-red-50 rounded text-red-600"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create User Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>Crea Nuovo Utente</CardTitle>
              <CardDescription>
                Inserisci i dati dell'utente. La password deve essere forte.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="username">Username *</Label>
                    <Input
                      id="username"
                      value={createForm.username}
                      onChange={(e) => setCreateForm({...createForm, username: e.target.value})}
                      placeholder="es. mario.rossi"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="full_name">Nome Completo *</Label>
                    <Input
                      id="full_name"
                      value={createForm.full_name}
                      onChange={(e) => setCreateForm({...createForm, full_name: e.target.value})}
                      placeholder="Mario Rossi"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={createForm.email}
                    onChange={(e) => setCreateForm({...createForm, email: e.target.value})}
                    placeholder="mario.rossi@azienda.it"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="role">Ruolo *</Label>
                  <select
                    id="role"
                    value={createForm.role}
                    onChange={(e) => setCreateForm({...createForm, role: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="operator">Operatore</option>
                    <option value="admin">Amministratore</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={createForm.password}
                    onChange={(e) => setCreateForm({...createForm, password: e.target.value})}
                    required
                  />
                  {createForm.password && (
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-medium ${getPasswordStrength(createForm.password).color}`}>
                        {getPasswordStrength(createForm.password).label}
                      </span>
                    </div>
                  )}
                  <p className="text-xs text-gray-600">
                    Min 8 caratteri, maiuscola, minuscola, numero e carattere speciale (!@#$%^&*...)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Conferma Password *</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={createForm.confirmPassword}
                    onChange={(e) => setCreateForm({...createForm, confirmPassword: e.target.value})}
                    required
                  />
                </div>

                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="flex-1">
                    Crea Utente
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsCreateModalOpen(false);
                      setCreateForm({
                        username: '',
                        email: '',
                        full_name: '',
                        password: '',
                        confirmPassword: '',
                        role: 'operator'
                      });
                    }}
                  >
                    Annulla
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Change Password Modal */}
      {isPasswordModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle>Cambia Password</CardTitle>
              <CardDescription>
                Aggiorna la tua password con una password forte
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="old_password">Password Attuale *</Label>
                  <Input
                    id="old_password"
                    type="password"
                    value={passwordForm.old_password}
                    onChange={(e) => setPasswordForm({...passwordForm, old_password: e.target.value})}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="new_password">Nuova Password *</Label>
                  <Input
                    id="new_password"
                    type="password"
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                    required
                  />
                  {passwordForm.new_password && (
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-medium ${getPasswordStrength(passwordForm.new_password).color}`}>
                        {getPasswordStrength(passwordForm.new_password).label}
                      </span>
                    </div>
                  )}
                  <p className="text-xs text-gray-600">
                    Min 8 caratteri, maiuscola, minuscola, numero e carattere speciale
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm_password">Conferma Nuova Password *</Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    value={passwordForm.confirm_password}
                    onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                    required
                  />
                </div>

                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="flex-1">
                    Cambia Password
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsPasswordModalOpen(false);
                      setPasswordForm({
                        old_password: '',
                        new_password: '',
                        confirm_password: ''
                      });
                    }}
                  >
                    Annulla
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
